<template>
  <div class="view-machine-report pad">
    <div class="flex pad-bottom">
      <div class="flex-1">
        <back-button />
      </div>
      <button class="button is-small" :class="{'is-loading': loading}" @click="print()">
        <font-awesome-icon icon="print" />
        &nbsp;
        Print
      </button>
    </div>
    <div class="box no-margin">
      <h1 class="title is-5">Options</h1>
      <div class="columns">
        <div class="column">
          <b-field label="Date From" horizontal>
            <b-datepicker
              v-model="dateFrom"
              :mobile-native="false"
              icon-pack="fas"
              placeholder="date from"
              icon="calendar-check"
            >
              <button class="button is-primary is-small" @click.prevent="dateFrom = new Date()">
                Today
              </button>
            </b-datepicker>
          </b-field>
        </div>
        <div class="column">
          <b-field label="Date To" horizontal>
            <b-datepicker
              v-model="dateTo"
              :mobile-native="false"
              icon-pack="fas"
              placeholder="date to"
              icon="calendar-check"
            >
              <button class="button is-primary is-small" @click.prevent="dateTo = new Date()">
                Today
              </button>
            </b-datepicker>
          </b-field>
        </div>
      </div>
      <div class="columns">
        <div class="column">
          <b-checkbox v-model="showDetails">Show Details</b-checkbox>
        </div>
        <div class="column">
          <b-checkbox v-model="showServices">Show Service History</b-checkbox>
        </div>
        <div class="column">
          <b-checkbox v-model="showRepairs">Show Current Repairs</b-checkbox>
        </div>
        <div class="column">
          <b-checkbox v-model="showPreStarts">Show Pre-Start Checks Lists</b-checkbox>
        </div>
      </div>
    </div>

    <div v-if="machine === null">
      <loading />
    </div>

    <div v-else-if="machine" class="machine-container" ref='printContainer'>

      <!-- DETIAIL -->
      <table class="page-break" v-if="showDetails">
        <thead class="letter-head"><tr><th><letter-head /></th></tr></thead>
        <tbody>
          <tr>
            <td>
              <h1 class="title is-3">{{ machine.name }} Details</h1>
              <table class="table is-bordered">
                <thead>
                  <tr>
                    <th class="capitalize" colspan="2">{{ machine.category ? machine.category : 'Machine'}} Details</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>Name</th>
                    <td>{{ machine.name }}</td>
                  </tr>
                  <tr v-if="machine.category && machine.category.length">
                    <th>Categpory</th>
                    <td>{{ machine.category }}</td>
                  </tr>
                  <tr>
                    <th>{{ machine.units | prettyUnits }}</th>
                    <td>
                      {{ machine.smu }} {{ machine.units}}
                      <span v-if="machine.timestampSmuUpdated > 0">
                        ({{ machine.timestampSmuUpdated | unixToDateTimeString }})
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th>Next Service</th>
                    <td>
                      {{ machine.nextService }} {{ machine.units}}
                      <span>{{ serviceMsg }}</span>
                    </td>
                  </tr>
                  <tr>
                    <th>Next Service Type</th>
                    <td>{{ machine.typeOfService }}</td>
                  </tr>
                  <tr>
                    <th>Service Interval</th>
                    <td>{{ machine.serviceInterval }} {{ machine.units}}</td>
                  </tr>
                  <tr>
                    <th>Service Warning</th>
                    <td>
                      {{ machine.serviceWarning }} {{ machine.units}}
                      <span>to next service</span>
                    </td>
                  </tr>
                  <tr  v-for="(d, i) in machine.otherDetails" :key="i">
                    <th>{{ d.key }}</th>
                    <td>{{ d.value }}</td>
                  </tr>
                  <tr>
                    <th>Descriptoin / Notes</th>
                    <td><div class="nl">{{ machine.description }}</div></td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- REPAIRS -->
      <loading v-if="repairs === null" />
      <table class="page-break" v-if="repairs">
        <thead class="letter-head"><tr><th><letter-head /></th></tr></thead>
        <tbody>
          <tr v-for="(r, index) in repairs" :key="r._id">
            <td>
              <h1 class="title is-3" v-if="index === 0">
                {{ machine.name }} Repairs
                <span v-if="timestampFrom">from {{ timestampFrom | unixToDateString }}</span>
                <span v-if="timestampTo">to {{ timestampTo | unixToDateString }}</span>
              </h1>
              <table class="table is-bordered">
                <thead>
                  <tr>
                    <th colspan="2">
                      <div class="flex">
                        <div class="flex-1">{{ machine.name }} {{ r.urgent ? 'URGENT' : '' }} Repair</div>
                        <div>{{ r.timestampReported | unixToDateTimeString }}</div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>Description</th>
                    <td>{{ r.description }}</td>
                  </tr>
                  <tr>
                    <th>Reported</th>
                    <td>
                      <div class="flex">
                        {{ r.timestampReported | unixToDateTimeString }} <span>by {{ r.reportedByUser }}</span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Fixed</th>
                    <td>
                      <div class="flex">
                        <template v-if="r.timestampFixed">
                          {{ r.timestampFixed | unixToDateTimeString }} <span>by {{ r.fixedByUser }}</span>
                        </template>
                        <template v-else>
                          -
                        </template>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Notes</th>
                    <td><div class="nl">{{ r.notes }}</div></td>
                  </tr>
                </tbody>
              </table>
              <br />
            </td>
          </tr>
        </tbody>
      </table>

      <!-- SEVICES -->
      <loading v-if="services === null" />
      <table class="page-break" v-if="services">
        <thead class="letter-head"><tr><th><letter-head /></th></tr></thead>
        <tbody>
          <tr v-for="(s, index) in services" :key="s._id">
            <td class="page-break-after-auto">
              <h1 class="title is-3" v-if="index === 0">
                {{ machine.name }} Services
                <span v-if="timestampFrom">from {{ timestampFrom | unixToDateString }}</span>
                <span v-if="timestampTo">to {{ timestampTo | unixToDateString }}</span>
              </h1>
              <table class="table is-bordered">
                <thead>
                  <tr>
                    <th colspan="2">
                      <div class="flex">
                        <div class="flex-1">{{ machine.name }} Service</div>
                        <div>{{ s.timestampStarted | unixToDateTimeString }}</div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>Status</th>
                    <td class="capitalise">{{ s.status }}</td>
                  </tr>
                  <tr>
                    <th>Started</th>
                    <td>
                      {{ s.timestampStarted | unixToDateTimeString }}
                      <span>by {{ s.userStarted }}</span>
                    </td>
                  </tr>
                  <tr>
                    <th>Completed</th>
                    <td>
                      <template v-if="s.timestampCompleted">
                        {{ s.timestampStarted | unixToDateTimeString }}
                        <span>by {{ s.userStarted }}</span>
                      </template>
                      <template v-else>
                        -
                      </template>
                    </td>
                  </tr>
                  <template v-if="s.checkList">
                    <tr class="check-list">
                      <td colspan="2" class="check-list">
                        <printable-check-list-summary :check-list="s.checkList" />
                      </td>
                    </tr>
                  </template>
                  <tr v-for="(n, index) in s.notes" :key="index" class="note">
                    <th>
                      <div>{{ n.timestamp | unixToDateTimeString }}</div>
                      <div>{{ n.user }}</div>
                    </th>
                    <td>
                      {{ n.msg }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <br />
            </td>
          </tr>
        </tbody>
      </table>

      <!-- PRE-STARTS -->
      <loading v-if="preStarts === null" />
      <table class="page-break" v-if="preStarts">
        <thead class="letter-head"><tr><th><letter-head /></th></tr></thead>
        <tbody>
          <tr v-for="(checkList, index) in preStarts" :key="checkList._id">
            <td>
              <h1 class="title is-3" v-if="index === 0">
                {{ machine.name }} Pre-Start Checks
                <span v-if="timestampFrom">from {{ timestampFrom | unixToDateString }}</span>
                <span v-if="timestampTo">to {{ timestampTo | unixToDateString }}</span>
              </h1>
              <printable-check-list-summary :check-list="checkList" />
              <br />
            </td>
          </tr>
        </tbody>
      </table>

    </div> <!-- if machine -->

  </div>
</template>

<script>
import PrintableCheckListSummary from '@/components/PrintableCheckListSummary'
import LetterHead from '@/components/LetterHead'
import { PrintUtils } from '@/utils/print.js'
export default {
  name: 'ViewMachineReport',
  components: {
    PrintableCheckListSummary,
    LetterHead
  },
  data () {
    return {
      machineId: null,
      machine: null,
      repairs: false,
      services: false,
      preStarts: false,
      dateFrom: null,
      dateTo: null,
      showDetails: true,
      showServices: true,
      showRepairs: false,
      showPreStarts: false
    }
  },
  computed: {
    loading () { return this.machine === null || this.services === null || this.repairs === null || this.preStarts === null },
    timestampFrom () { return this.dateFrom ? this.$moment(this.dateFrom).unix() : 0 },
    timestampTo () { return this.dateTo ? this.$moment(this.dateTo).unix() : 0 },
    serviceDue () {
      return this.machine.smu >= (this.machine.nextService - this.machine.serviceWarning)
    },
    serviceOverDue () {
      return this.machine.smu > this.machine.nextService
    },
    serviceMsg () {
      if (!this.machine && this.machine.nextService) return ''
      let d = Math.abs(this.machine.smu - this.machine.nextService)
      if (this.serviceOverDue) {
        return `(over due ${d} ${this.machine.units})`
      } else if (this.serviceDue) {
        return `(due in ${d} ${this.machine.units})`
      } else {
        return `(in ${d} ${this.machine.units})`
      }
    }
  },
  watch: {
    machineId () { this.getMachine() },
    showRepairs () { this.getRepairs() },
    showServices () { this.getServices() },
    showPreStarts () { this.getPreStarts() },
    timestampFrom () { this.getAll() },
    timestampTo () { this.getAll() }
  },
  beforeRouteUpdate (to, from, next) {
    this.machineId = to.params.id
    next()
  },
  created () {
    this.machineId = this.$route.params.id
    this.getAll()
  },
  methods: {
    getAll () {
      this.getRepairs()
      this.getServices()
      this.getPreStarts()
    },
    getMachine () {
      this.machine = null
      if (!this.machineId) {
        this.machine = false
        return
      }

      this.$firestore.collection('machines')
        .doc(this.machineId)
        .get()
        .then(doc => {
          this.machine = this.$utils.docToObj(doc)
          this.getAll()
        })
        .catch(err => {
          this.machine = false
          this.$toast.open({ message: 'ERROR could not get machine', type: 'is-danger' })
          console.log('ERROR getting machine', err)
        })
    },
    getRepairs () {
      if (!this.showRepairs || !this.machine) {
        this.repairs = false
        return
      }
      this.repairs = null
      let docRef = this.$firestore.collection('machineRepairs')
        .where('machineId', '==', this.machine._id)

      if (this.timestampFrom) {
        docRef = docRef.where('timestampReported', '>=', this.timestampFrom)
      }
      if (this.timestampTo) {
        docRef = docRef.where('timestampReported', '<=', this.timestampTo)
      }
      docRef = docRef.orderBy('timestampReported', 'desc')

      docRef.get()
        .then(snapshot => {
          this.repairs = this.$utils.snapshotToArr(snapshot)
        })
        .catch(err => {
          console.log('ERROR getting repairs', err)
        })
    },
    getServices () {
      if (!this.showServices || !this.machine) {
        this.services = false
        return
      }
      this.services = null
      let docRef = this.$firestore.collection('machineServices')
        .where('machineId', '==', this.machine._id)

      if (this.timestampFrom) {
        docRef = docRef.where('timestampStarted', '>=', this.timestampFrom)
      }
      if (this.timestampTo) {
        docRef = docRef.where('timestampStarted', '<=', this.timestampTo)
      }
      docRef = docRef.orderBy('timestampStarted', 'desc')

      docRef.get()
        .then(snapshot => {
          this.services = this.$utils.snapshotToArr(snapshot)
        })
        .catch(err => {
          console.log('ERROR getting services', err)
        })
    },
    getPreStarts () {
      if (!this.showPreStarts || !this.machine) {
        this.preStarts = false
        return
      }
      this.preStarts = null
      let docRef = this.$firestore.collection('preStartChecks')
        .where('machineId', '==', this.machine._id)

      if (this.timestampFrom) {
        docRef = docRef.where('timestampStarted', '>=', this.timestampFrom)
      }
      if (this.timestampTo) {
        docRef = docRef.where('timestampStarted', '<=', this.timestampTo)
      }
      docRef = docRef.orderBy('timestampStarted', 'desc')

      docRef.get()
        .then(snapshot => {
          this.preStarts = this.$utils.snapshotToArr(snapshot)
        })
        .catch(err => {
          console.log('ERROR getting pre start checks', err)
        })
    },
    print () {
      let html = `${this.$refs.printContainer.innerHTML}`
      let title = `${this.machine.name} Report`
      let css = `
        table.table tr th {
          width: 180px;
          -webkit-print-color-adjust: exact;
          background-color: #eee;
        }
        
        td.check-list { padding: 0 !important; }
        table.printable-check-list-summary { margin: 0 !important; }
        table.printable-check-list-summary tbody tr th { width: 50%; }
        .letter-head { display: table-header-group; }
        td.page-break-after-auto { }
      `
      PrintUtils.print({
        html,
        title,
        css,
        pageStyle: true
      })
    }
  }
}
</script>

<style>
  .machine-container h1 {
    margin-top: 40px;
  }

  .machine-container table.table tbody tr th {
    width: 180px;
    background-color: #eee;
  }

  .machine-container span {
    padding-left: 20px;
    color: hsl(0, 0%, 48%);
  }

  .machine-containertable.table tbody tr td.check-list {
    padding: 0;
    border-top: solid 2px #999;
    border-bottom: solid 2px #999;
  }

  .machine-container table.table tbody tr td.check-list {
    padding: 0;
  }

  .machine-container table.table tbody tr td.check-list table.printable-check-list-summary {
    margin: 0;
  }

  .machine-container table.printable-check-list-summary tbody tr th {
    width: 50% ;
  }

  .machine-container tr.note th {
    font-size: 0.75em;
  }

  .letter-head {
    display: none;
  }

  .view-machine-report .machine-container h1 {
    margin-top: 50px;
  }

  td.page-break-after-auto {
    page-break-after: auto;
    page-break-before: auto;
  }
</style>
